import { actionPlanV2Api, type AiCreateTaskResponse, type AiDeleteTaskResponse } from "@/services/action-plan-v2";
import type { ActionPlanAiPendingPlan } from "@/types/action-plan-v2/ActionPlanAiPlan";
import { ACTION_PLAN_V2 } from "@/components/common/ActionPlanV2/constants";
import { toActionPlanApiDateIso } from "@/utils/dateOnlyForApi";
import type { ApiResponse } from "@/types/base";
import { aiAgentApi } from "@/services/knowledge/ai-agent-api";
import { agentRunClient } from "@/services/knowledge/agent-run-client";

/** Runtime: interceptor trả envelope ApiResponse; kiểu axios vẫn ghi AxiosResponse → unwrap qua unknown. */
export function unwrapApiResponse<T>(raw: unknown): ApiResponse<T> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as ApiResponse<T>;
  return typeof o.success === "boolean" ? o : undefined;
}

function createdIdFromCreateResponse(res: ApiResponse<AiCreateTaskResponse> | undefined): string {
  const data = res?.data as AiCreateTaskResponse | Record<string, unknown> | undefined;
  if (!data) return "";
  const id = "id" in data ? data.id : undefined;
  const alt = "Id" in data ? (data as Record<string, unknown>).Id : undefined;
  return String(id ?? alt ?? "").trim();
}

/** Lệnh do trợ lý đề xuất (điểm chờ duyệt của run) — engine ghi khi duyệt. */
export type ActionPlanAiInterruptRef = { runId?: string; interruptId?: string; edited?: boolean };

/**
 * Xác nhận thẻ tạo / sửa / xoá việc: engine (MS.AICoaching) ghi vào MS.ActionPlan bằng JWT người dùng.
 * - Plan từ trợ lý: POST /runs/{runId}/resume (approve, hoặc edit khi người dùng sửa thẻ).
 * - Thẻ tạo ở FE (chọn mục tiêu, Sửa / Việc con từ danh sách): POST /action-plan/execute.
 */
export async function confirmActionPlanAiPlan(
  plan: ActionPlanAiPendingPlan,
  ref?: ActionPlanAiInterruptRef,
): Promise<string> {
  if (plan.mustManual || plan.action === "manual") {
    throw new Error(plan.reason || "Thao tác này cần làm thủ công trên Action Plan.");
  }
  const normalized = {
    ...plan,
    year: plan.year ?? new Date().getFullYear(),
    phanHe: plan.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
    ngayBatDau: toActionPlanApiDateIso(plan.ngayBatDau) ?? plan.ngayBatDau,
    ngayKetThuc: toActionPlanApiDateIso(plan.ngayKetThuc) ?? plan.ngayKetThuc,
  };
  if (ref?.runId && ref.interruptId) {
    const res = await agentRunClient.resume(ref.runId, {
      interrupt_id: ref.interruptId,
      decision: ref.edited ? "edit" : "approve",
      payload: ref.edited ? { plan: normalized } : undefined,
    });
    if (res.status !== "approved" || !res.result?.ok) {
      throw new Error(res.result?.error || "Không lưu được.");
    }
    return String(res.result.done?.[0]?.id ?? plan.taskId ?? "");
  }
  const res = await aiAgentApi.executeActionPlan(normalized as unknown as Record<string, unknown>);
  if (!res?.success || !res.data?.ok) {
    throw new Error(res?.message || "Không lưu được.");
  }
  return String(res.data.id ?? plan.taskId ?? "");
}

/** Ghi thẳng từ trình duyệt (cách cũ) — giữ cho trường hợp engine chưa có /action-plan/execute. */
export async function confirmActionPlanAiPlanDirect(plan: ActionPlanAiPendingPlan): Promise<string> {
  if (plan.mustManual || plan.action === "manual") {
    throw new Error(plan.reason || "Thao tác này cần làm thủ công trên Action Plan.");
  }
  if (plan.action === "create") {
    const raw = await actionPlanV2Api.aiCreateTask({
      idChoice: String(plan.idChoice || plan.nguoiPhuTrachId || "").trim(),
      year: plan.year ?? new Date().getFullYear(),
      phanHe: plan.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
      mucTieuId: String(plan.mucTieuId || "").trim(),
      tenCongViec: String(plan.tenCongViec || "").trim(),
      moTa: plan.moTa,
      nguoiPhuTrachId: String(plan.nguoiPhuTrachId || plan.idChoice || "").trim(),
      ngayBatDau:
        toActionPlanApiDateIso(plan.ngayBatDau) ??
        toActionPlanApiDateIso(new Date()) ??
        "",
      ngayKetThuc: toActionPlanApiDateIso(plan.ngayKetThuc) ?? "",
      parentId: plan.parentId || undefined,
    });
    const res = unwrapApiResponse<AiCreateTaskResponse>(raw);
    const newId = createdIdFromCreateResponse(res);
    if (!res?.success || !newId) {
      throw new Error(res?.message || "Không tạo được công việc.");
    }
    return newId;
  }
  if (plan.action === "update") {
    const taskId = String(plan.taskId || "").trim();
    if (!taskId) throw new Error("Thiếu mã công việc.");
    const raw = await actionPlanV2Api.aiUpdateTask({
      taskId,
      idChoice: String(plan.idChoice || plan.nguoiPhuTrachId || "").trim(),
      year: plan.year ?? new Date().getFullYear(),
      phanHe: plan.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
      tenCongViec: String(plan.tenCongViec || "").trim(),
      moTa: plan.moTa,
      ngayBatDau: toActionPlanApiDateIso(plan.ngayBatDau) ?? undefined,
      ngayKetThuc: toActionPlanApiDateIso(plan.ngayKetThuc) ?? undefined,
    });
    const res = unwrapApiResponse<{ id: string }>(raw);
    if (!res?.success) {
      throw new Error(res?.message || "Không cập nhật được công việc.");
    }
    return taskId;
  }
  if (plan.action === "delete") {
    const taskId = String(plan.taskId || "").trim();
    if (!taskId) throw new Error("Thiếu mã công việc.");
    const raw = await actionPlanV2Api.aiDeleteTask({
      taskId,
      idChoice: String(plan.idChoice || "").trim(),
      year: plan.year ?? new Date().getFullYear(),
      phanHe: plan.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
      confirm: true,
    });
    const res = unwrapApiResponse<AiDeleteTaskResponse>(raw);
    if (!res?.success || !res.data?.deleted) {
      throw new Error(res?.message || "Không xoá được công việc.");
    }
    return taskId;
  }
  throw new Error("Chưa hỗ trợ xác nhận loại thao tác này.");
}
